import callAnswer from "@/assets/sounds/call-answer.mp3";
import callHangUp from "@/assets/sounds/call-hang-up.mp3";
import callHold from "@/assets/sounds/call-hold.mp3";
import callResume from "@/assets/sounds/call-resume.mp3";
import ringingInbound from "@/assets/sounds/ringing-inbound.mp3";
import ringingOutbound from "@/assets/sounds/ringing-outbound.mp3";
import Announcement from "@/components/announcement";
import Call from "@/components/call";
import Devices from "@/components/layout/devices";
import EventLogger from "@/components/layout/event-logger";
import Header from "@/components/layout/header";
import SessionLoader from "@/components/layout/session-loader";
import WelcomeModal from "@/components/layout/welcome-modal";
import Wrapper from "@/components/layout/wrapper";
import { AppSidebar } from "@/components/sidebar/sidebar";
import SidebarTrigger from "@/components/sidebar/trigger";
import { getAppConfig } from "@/config/app-config";
import LayoutContextProvider from "@/contexts/layout/layout.context-provider";
import { useCallCenterSession } from "@/contexts/staff-session/staff-session.context";
import History from "@/routes/history";
import { CallCenter } from "@EspaceParkQC/sdk-call-center";
import { SidebarInset, SidebarProvider, Toaster, TooltipProvider } from "@EspaceParkQC/sdk-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function App(): React.ReactElement {
  const { user, getToken } = useCallCenterSession();
  const { t } = useTranslation("main");

  const [isModalDismissed, setIsModalDismissed] = useState(false);

  if (!user) {
    return (
      <LayoutContextProvider>
        <div className="flex items-center justify-center w-full h-full">
          <p>{t("session.loading")}</p>
        </div>
      </LayoutContextProvider>
    );
  }

  if (!user.sipInfo.username || !user.sipInfo.password || !user.sipInfo.identity || !user.sipInfo.registrar) {
    return (
      <LayoutContextProvider>
        <div className="flex items-center justify-center w-full h-full">
          <p>{t("session.loading-user-info")}</p>
        </div>
      </LayoutContextProvider>
    );
  }

  const sipConfig = {
    authuser: user.sipInfo.username,
    displayName: user.fullName ?? "",
    proxy: user.sipInfo.registrar,
    request: "register",
    secret: user.sipInfo.password,
    username: user.sipInfo.identity,
  };

  const appConfig = getAppConfig();

  return (
    <TooltipProvider delayDuration={0}>
      <LayoutContextProvider>
        <CallCenter
          clicknpark={{
            url: appConfig.VITE_CLICKNPARK_API_URL,
            getToken,
          }}
          janus={{
            server: appConfig.VITE_JANUS_SERVER_URL,
            registerConfig: sipConfig,
            autoConnect: true,
          }}
          notificationFiles={{
            "calling-in": ringingInbound,
            "calling-out": ringingOutbound,
            "answer": callAnswer,
            "decline": callHangUp,
            "hangup": callHangUp,
            "hold": callHold,
            "resume": callResume,
          }}
        >
          <SidebarProvider defaultOpen>
            <AppSidebar />
            <SidebarInset>
              <SidebarTrigger />
              <Announcement />
              <WelcomeModal open={!isModalDismissed} onDismiss={() => setIsModalDismissed(true)} />
              <Wrapper>
                <Header />
                <Devices />
                <SessionLoader>
                  <History />
                  <Call />
                </SessionLoader>
              </Wrapper>
              <Toaster />
            </SidebarInset>
          </SidebarProvider>
          <EventLogger />
        </CallCenter>
      </LayoutContextProvider>
    </TooltipProvider>
  );
}
